import React, { useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Truck, Sparkles, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Modal para marcar una línea de envío como recogida por el transportista.
 * Esto dispara automáticamente la creación de un albarán pendiente_facturar.
 *
 * Props:
 *  - linea: { id, descripcion, tipo, ... } | null
 *  - onClose: () => void
 *  - onSuccess: () => void  (recargar petición tras éxito)
 */
export default function EnvioRecogidoDialog({ linea, onClose, onSuccess }) {
  const today = new Date().toISOString().slice(0, 10);
  const [fechaEnvio, setFechaEnvio] = useState(today);
  const [transportista, setTransportista] = useState("GLS");
  const [tracking, setTracking] = useState("");
  const [autoAlbaran, setAutoAlbaran] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  if (!linea) return null;

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post(`/lineas/${linea.id}/marcar-envio-recogido`, {
        fecha_envio: fechaEnvio,
        transportista,
        tracking: tracking || null,
        auto_albaran: autoAlbaran,
      });
      if (data?.albaran_id) {
        toast.success(`Envío registrado · Albarán ${data.albaran_id.slice(0, 8)}… creado automáticamente`);
      } else {
        toast.success("Envío registrado");
      }
      onSuccess?.();
      onClose();
    } catch (e) {
      toast.error("Error: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={!!linea} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Truck className="w-5 h-5 text-fuchsia-400" />
            Marcar envío recogido
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="bg-zinc-900/60 border border-zinc-800 rounded p-3 text-sm">
            <div className="text-zinc-100 truncate">{linea.descripcion || "(sin descripción)"}</div>
            <div className="text-xs text-zinc-500 mt-1">Tipo: {linea.tipo}</div>
          </div>

          <div>
            <Label className="text-xs text-zinc-400">Fecha de recogida</Label>
            <Input type="date" value={fechaEnvio} onChange={(e) => setFechaEnvio(e.target.value)}
              data-testid="envio-fecha"
              className="bg-zinc-900 border-zinc-800 mt-1" />
          </div>

          <div>
            <Label className="text-xs text-zinc-400">Transportista</Label>
            <Select value={transportista} onValueChange={setTransportista}>
              <SelectTrigger data-testid="envio-transportista" className="bg-zinc-900 border-zinc-800 mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                <SelectItem value="GLS">GLS</SelectItem>
                <SelectItem value="Tipsa">Tipsa</SelectItem>
                <SelectItem value="Seur">Seur</SelectItem>
                <SelectItem value="MRW">MRW</SelectItem>
                <SelectItem value="Correos Express">Correos Express</SelectItem>
                <SelectItem value="Nacex">Nacex</SelectItem>
                <SelectItem value="Otro">Otro</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs text-zinc-400">Nº seguimiento (opcional)</Label>
            <Input value={tracking} onChange={(e) => setTracking(e.target.value)}
              placeholder="Ej: GLS123456789ES"
              data-testid="envio-tracking"
              className="bg-zinc-900 border-zinc-800 mt-1" />
          </div>

          <label className="flex items-start gap-2 bg-violet-500/5 border border-violet-500/20 rounded p-3 cursor-pointer">
            <input type="checkbox" checked={autoAlbaran} onChange={(e) => setAutoAlbaran(e.target.checked)}
              data-testid="envio-auto-albaran"
              className="mt-0.5 accent-fuchsia-500" />
            <div className="flex-1 text-xs text-violet-100">
              <div className="flex items-center gap-1 mb-0.5">
                <Sparkles className="w-3 h-3 text-violet-400" />
                <b>Crear albarán automáticamente</b>
              </div>
              Generamos albarán <span className="font-mono text-amber-300">ENV-YYYY-NNNN</span> en estado{" "}
              <b>pendiente_facturar</b>, listo para que Sara emita la factura. Esto previene que
              envíos sin montaje se queden sin facturar en el limbo.
            </div>
          </label>

          {!autoAlbaran && (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded p-2 text-[11px] text-amber-200 flex gap-2">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              <span>Sin albarán automático, tendrás que crearlo manualmente para facturar este trabajo.</span>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onClose} disabled={submitting}>Cancelar</Button>
          <Button onClick={submit} disabled={submitting}
            data-testid="envio-submit"
            className="bg-fuchsia-500 hover:bg-fuchsia-400 text-zinc-950 font-semibold">
            {submitting ? "Procesando…" : <><CheckCircle2 className="w-4 h-4 mr-1.5" /> Confirmar recogida</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
